import { useConfirm } from 'legacy/lut/components/shared/Confirm/useConfirm'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { FC, useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { NextButton } from 'partner360/components/QBR/components/qbrDocumentDetails/components/nextButton/NextButton'
import { addInitiatives, removeInitiative, selectQBREntryId } from 'partner360/features/QBR'
import { selectQBRSelectedDocumentInitiatives } from 'partner360/features/QBR/selectors/selectQBRSelectedDocumentInitiatives'
import { IQbrDocumentInitiative } from 'partner360/models/qbr/iQbrDocumentInitiative'

import { useQbrSave } from '../../../hooks/useQbrSave'
import { QBRErrorModal } from '../settingsStep/components/qbrErrorModal/QBRErrorModal'

import { AddInitiativeCard } from './components/addInitiativeCard/AddInitiativeCard'
import {
  DELETE_INITIATIVE_CONFIRM_IDENTIFIER,
  DeleteInitiativeConfirm,
} from './components/deleteInitiativeConfirm'
import { InitiativeCard } from './components/initiativeCard'
import { InitiativesModal } from './components/initiativesModal/InitiativesModal'
import { useAvailableInitiatives } from './hooks/useAvailableInitiatives'

import styles from './Initiatives.module.scss'

export const Initiatives: FC = () => {
  /* ------------------ State ------------------ */
  const [isInitiativesModalShown, setIsInitiativeModalShown] = useState(false)

  /* ------------------ Selectors ------------------ */
  const documentId = useSelector(selectQBREntryId)
  const qbrDocumentInitiatives = useSelector(selectQBRSelectedDocumentInitiatives)

  /* ------------------ Hooks ------------------ */
  const dispatch = useAppDispatch()
  const { isLoading, availableInitiatives, initiativesCrud } = useAvailableInitiatives(documentId)
  const { confirm } = useConfirm()
  const { onNextButtonClick, isSavingProgress, showErrorModal, onCloseErrorModal } = useQbrSave()

  /* ------------------ Methods ------------------ */
  const handleOpenInitiativesDialog = useCallback(() => {
    setIsInitiativeModalShown(true)
  }, [])

  const handleCloseInitiativesDialog = useCallback(() => {
    setIsInitiativeModalShown(false)
  }, [])

  const handleInitiativeRemove = useCallback(
    async (initiativeId: number) => {
      const shouldRemove = await confirm(DELETE_INITIATIVE_CONFIRM_IDENTIFIER)
      if (shouldRemove) {
        dispatch(removeInitiative(initiativeId))
      }
    },
    [dispatch]
  )

  const handleAddInitiatives = useCallback(
    (initiatives: IQbrDocumentInitiative[]) => {
      dispatch(addInitiatives(initiatives))
      handleCloseInitiativesDialog()
    },
    [dispatch, handleCloseInitiativesDialog]
  )

  /* ------------------ Layout ------------------ */
  return (
    <div className={styles.root}>
      <DeleteInitiativeConfirm />
      <div className={styles.initiativesContent}>
        {qbrDocumentInitiatives.map((initiative) => (
          <InitiativeCard
            key={initiative.EntryId}
            initiativeData={initiative}
            onInitiativeRemove={handleInitiativeRemove}
          />
        ))}
        <AddInitiativeCard
          isLoading={isLoading}
          initiativesCount={availableInitiatives.length}
          onClick={handleOpenInitiativesDialog}
        />
        <InitiativesModal
          dataSource={initiativesCrud}
          isOpen={isInitiativesModalShown}
          onAddInitiatives={handleAddInitiatives}
          onClose={handleCloseInitiativesDialog}
        />
      </div>
      <NextButton onClick={onNextButtonClick} />
      <QBRErrorModal
        show={showErrorModal}
        onCloseButtonClick={onCloseErrorModal}
        isButtonDisabled={isSavingProgress}
      />
    </div>
  )
}
